// `desc` reference form — wraps a `ColumnRef` to flag descending order
// for the `sort` verb. Standalone op so the LLM can write
// `(desc (col my-ds revenue))` or, inside a `sort`, the shorter
// `(desc revenue)` where `revenue` is a bare column of the threaded
// dataset.
//
// Two callers paths:
//
//   - **Outside `sort`** — eager apply takes a resolved `ColumnRef` and
//     wraps it as a `DescSpec`. Useful for binding `(dataset
//     my-sort-key (desc (col foo bar)))` and similar.
//   - **Inside `sort`** — sort's apply inspects the raw AST and
//     short-circuits the eager path when the inner arg is a bare
//     symbol (which would otherwise fail to resolve to a `ColumnRef`).
//     This lets `(sort (desc bare-col))` work without forcing the LLM
//     to write `(sort (desc (col threaded-ds bare-col)))`.

import { z } from 'zod';

import { opMeta } from '../../op-meta.ts';
import type { Value } from '../../value.ts';
import { ColumnRef, DescSpec } from '../../value.ts';

/**
 * `(desc <col-ref>)` — sort-direction wrapper. Eager apply: the Zod
 * schema validates the arg is a `ColumnRef`; the apply wraps it as
 * a `DescSpec`. Sort's apply (see `verbs/sort.ts`) inspects the raw
 * AST and bypasses this apply when the inner arg is a bare symbol
 * (which would not resolve to a `ColumnRef` on its own) — that case
 * never reaches here.
 */
export const desc = z
  .object({
    name: z.literal('desc'),
    params: z.tuple([z.custom<ColumnRef>((v) => v instanceof ColumnRef, 'expected a ColumnRef')]),
  })
  .describe(
    'Mark a column for descending sort order. Inside `(sort …)` or a `:sort` vector the common form is ' +
      '`(desc <column>)` over a bare column name. Standalone — e.g. bound via `(dataset k (desc (col ds c)))` ' +
      '— it wraps a `(col …)` reference into a sort-direction value. Example: `(sort name (desc capacity))`.',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(sort (desc capacity))' },
    apply: ({ params: [col] }): Value => new DescSpec(col),
  });
